
import { useState } from "react";
import { RecipeIdea } from "../types";
import { useRecipeFetch } from "./useRecipeFetch";
import { useRecipeOperations } from "./useRecipeOperations";

export const useRecipeManager = () => {
  const { recipes, setRecipes, isLoading } = useRecipeFetch();
  const { handleDeleteRecipe, handleSaveRecipe } = useRecipeOperations(recipes, setRecipes);
  
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRecipe, setSelectedRecipe] = useState<RecipeIdea | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [viewMode, setViewMode] = useState<"list" | "form">("list");
  
  // Filter recipes based on search term
  const filteredRecipes = recipes.filter(recipe => 
    recipe.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    recipe.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    recipe.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
    recipe.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Handle creating a new recipe
  const handleCreateRecipe = () => {
    setSelectedRecipe(null);
    setIsEditing(true);
    setViewMode("form");
  };
  
  // Handle editing a recipe
  const handleEditRecipe = (recipe: RecipeIdea) => {
    setSelectedRecipe(recipe);
    setIsEditing(true);
    setViewMode("form");
  };
  
  // Wrapper for handleDeleteRecipe
  const handleRecipeDelete = (id: string) => {
    handleDeleteRecipe(id);
  };
  
  // Wrapper for handleSaveRecipe
  const handleRecipeSave = async (recipeData: Omit<RecipeIdea, 'id' | 'createdAt' | 'updatedAt'>) => {
    const success = await handleSaveRecipe(recipeData, selectedRecipe);
    
    if (success) {
      // Reset form state
      setSelectedRecipe(null);
      setIsEditing(false);
      setViewMode("list");
    }
  };

  // Handle going back to the list view
  const handleBackToList = () => {
    setSelectedRecipe(null);
    setIsEditing(false);
    setViewMode("list");
  };

  return {
    recipes,
    filteredRecipes,
    isLoading,
    searchTerm,
    setSearchTerm,
    selectedRecipe,
    setSelectedRecipe,
    isEditing,
    setIsEditing,
    viewMode,
    setViewMode,
    handleCreateRecipe,
    handleEditRecipe,
    handleDeleteRecipe: handleRecipeDelete,
    handleSaveRecipe: handleRecipeSave,
    handleBackToList
  };
};

export default useRecipeManager;
